import { useState, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetVehicleParkReportQuery } from '../../../../api/endpoints/vehicleParkReport';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';
import { formatDateForApi } from '../../../../utils/dateUtils';

/* ------------------------------------------------------------------ */
/*  Initial state                                                       */
/* ------------------------------------------------------------------ */
const INITIAL_FILTER_STATE = {
    /* text fields */
    sasiNo: null,
    sasiNoMatchMode: null,
    pypKop: null,
    pypKopMatchMode: null,
    model: null,
    modelMatchMode: null,
    urunAciklama: null,
    urunAciklamaMatchMode: null,
    garantiKm: null,
    garantiKmMatchMode: null,
    garantiMonth: null,
    garantiMonthMatchMode: null,
    musteriBilgisi: null,
    musteriBilgisiMatchMode: null,
    bayiBilgisi: null,
    bayiBilgisiMatchMode: null,
    sevkUlke: null,
    sevkUlkeMatchMode: null,
    /* date fields */
    garantiBaslangicTarihi: null,
    garantiBaslangicTarihiMatchMode: FilterMatchMode.DATE_IS,
    standartGarantiBitisTarihi: null,
    standartGarantiBitisTarihiMatchMode: FilterMatchMode.DATE_IS,
    faturaTarihi: null,
    faturaTarihiMatchMode: FilterMatchMode.DATE_IS,
    montajCikisTarih: null,
    montajCikisTarihMatchMode: FilterMatchMode.DATE_IS,
    uzatilmisGarantiBitisTarihi: null,
    uzatilmisGarantiBitisTarihiMatchMode: FilterMatchMode.DATE_IS,
    bataryaStandartGarantiBitisTarihi: null,
    bataryaStandartGarantiBitisTarihiMatchMode: FilterMatchMode.DATE_IS,
    bataryaUzatilmisGarantiBitisTarihi: null,
    bataryaUzatilmisGarantiBitisTarihiMatchMode: FilterMatchMode.DATE_IS,
};

/* ------------------------------------------------------------------ */
/*  Backend column map  (camelCase -> PascalCase)                       */
/* ------------------------------------------------------------------ */
const backendColumnMap = {
    sasiNo: 'SasiNo',
    pypKop: 'PypKop',
    model: 'Model',
    urunAciklama: 'UrunAciklama',
    garantiKm: 'GarantiKm',
    garantiBaslangicTarihi: 'GarantiBaslangicTarihi',
    standartGarantiBitisTarihi: 'StandartGarantiBitisTarihi',
    montajCikisTarih: 'MontajCikisTarih',
    uzatilmisGarantiBitisTarihi: 'UzatilmisGarantiBitisTarihi',
    bataryaStandartGarantiBitisTarihi: 'BataryaStandartGarantiBitisTarihi',
    bataryaUzatilmisGarantiBitisTarihi: 'BataryaUzatilmisGarantiBitisTarihi',
    faturaTarihi: 'FaturaTarihi',
    musteriBilgisi: 'MusteriBilgisi',
    bayiBilgisi: 'BayiBilgisi',
    sevkUlke: 'SevkUlke',
    garantiMonth: 'GarantiMonth',
};

const getBackendColumnName = (field) => backendColumnMap[field] || null;

const getOperatorValue = (matchMode) => matchModeToOperator[matchMode] ?? matchModeToOperator[FilterMatchMode.CONTAINS];

/* ------------------------------------------------------------------ */
/*  Build filters array for API body                                    */
/* ------------------------------------------------------------------ */
const DATE_FIELD_NAMES = new Set(['garantiBaslangicTarihi', 'standartGarantiBitisTarihi', 'faturaTarihi', 'montajCikisTarih', 'uzatilmisGarantiBitisTarihi', 'bataryaStandartGarantiBitisTarihi', 'bataryaUzatilmisGarantiBitisTarihi']);

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    const pushFilter = (field, value, matchMode) => {
        const column = getBackendColumnName(field);
        if (!column) return;

        let filterValue;
        if (DATE_FIELD_NAMES.has(field)) {
            filterValue = formatDateForApi(value);
        } else {
            if (value === null || value === undefined || value === '') return;
            filterValue = String(value).trim();
        }

        if (!filterValue) return;
        filters.push({ column, operator: getOperatorValue(matchMode), value: filterValue });
    };

    Object.keys(backendColumnMap).forEach((field) => {
        pushFilter(field, filtersState[field], filtersState[`${field}MatchMode`]);
    });

    return filters;
};

/* ------------------------------------------------------------------ */
/*  Build sorts array for API body                                      */
/* ------------------------------------------------------------------ */
const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = getBackendColumnName(field);
    if (!column) return null;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

/* ------------------------------------------------------------------ */
/*  localStorage persistence                                            */
/* ------------------------------------------------------------------ */
const STORAGE_KEY = 'vehicleParkReportFilters';

const loadFiltersFromStorage = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : null;
    } catch {
        return null;
    }
};

const saveFiltersToStorage = (filters, sortField, sortOrder, lazyParams) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ filters, sortField, sortOrder, lazyParams }));
    } catch {
        // ignore storage errors
    }
};

/* ------------------------------------------------------------------ */
/*  Hook                                                                */
/* ------------------------------------------------------------------ */
const useVehicleParkReportData = () => {
    const { getCurrentLanguageId } = useLanguage();
    const initialStoredData = loadFiltersFromStorage();

    const [lazyParams, setLazyParams] = useState(() => initialStoredData?.lazyParams || { first: 0, rows: 20, page: 1 });
    const [sortField, setSortField] = useState(() => initialStoredData?.sortField || null);
    const [sortOrder, setSortOrder] = useState(() => initialStoredData?.sortOrder || 0);
    const [activeFilters, setActiveFilters] = useState(() => initialStoredData?.filters || INITIAL_FILTER_STATE);

    const queryParams = useMemo(() => {
        const params = {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows
        };
        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;
        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts) params.sorts = sorts;
        return params;
    }, [activeFilters, lazyParams.page, lazyParams.rows, sortField, sortOrder]);

    const exportParams = useMemo(() => {
        const params = { compressionType: 1, LanguageId: getCurrentLanguageId() };
        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;
        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts) params.sorts = sorts;
        return params;
    }, [activeFilters, sortField, sortOrder, getCurrentLanguageId]);

    const { data: apiData, isLoading, isFetching } = useGetVehicleParkReportQuery(queryParams);

    /* ---- Handlers ---- */
    const handleFilterChange = (updatedFilters) => {
        setActiveFilters(updatedFilters);
        const newLazyParams = { first: 0, rows: lazyParams.rows, page: 1 };
        setLazyParams(newLazyParams);
        saveFiltersToStorage(updatedFilters, sortField, sortOrder, newLazyParams);
    };

    const initFilters = () => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        const newLazyParams = { first: 0, rows: 20, page: 1 };
        setLazyParams(newLazyParams);
        saveFiltersToStorage(INITIAL_FILTER_STATE, null, 0, newLazyParams);
    };

    const onPage = (event) => {
        const newLazyParams = { first: event.first, rows: event.rows, page: event.page + 1 };
        setLazyParams(newLazyParams);
        saveFiltersToStorage(activeFilters, sortField, sortOrder, newLazyParams);
    };

    const handleSortChange = (event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;
        setSortField(nextSortField);
        setSortOrder(nextSortOrder);
        const newLazyParams = { first: 0, rows: lazyParams.rows, page: 1 };
        setLazyParams(newLazyParams);
        saveFiltersToStorage(activeFilters, nextSortField, nextSortOrder, newLazyParams);
    };

    return {
        apiData,
        isLoading: isLoading || isFetching,
        lazyParams,
        sortField,
        sortOrder,
        activeFilters,
        exportParams,
        onPage,
        onSort: handleSortChange,
        handleFilterChange,
        initFilters
    };
};

export default useVehicleParkReportData;
